import React, { useState, useMemo } from 'react';
import { MessageSquare, Send } from 'lucide-react'; // Iconos de Lucide React
import { useCreateComment } from '../../functions/commentHook';

interface DiscussionSectionProps {
  blogId: string;
}

const DiscussionSection: React.FC<DiscussionSectionProps> = ({ blogId }) => {
  const [comment, setComment] = useState('');
  const { mutate: createComment, isLoading, isSuccess, error } = useCreateComment();

  const handleCommentSubmit = () => {
    if (!comment.trim()) return; // Evitar enviar comentarios vacíos

    // Crear el comentario en el servidor
    createComment({ blogId, commentData: { content: comment } });

    // Limpiar el campo de texto
    setComment('');
  };

  // Usamos useMemo para memorizar el mensaje de éxito o error y evitar renders innecesarios
  const resultMessage = useMemo(() => {
    if (isSuccess) {
      return <p className="text-green-500 mt-2">Comment added successfully!</p>;
    }
    if (error) {
      return <p className="text-red-500 mt-2">{error}</p>;
    }
    return null;
  }, [isSuccess, error]); // Solo se recalculate cuando isSuccess o error cambien

  // Función para manejar el evento de tecla Enter
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { // Evitar enviar con Shift+Enter (para salto de línea)
      e.preventDefault(); // Evitar un salto de línea
      handleCommentSubmit(); // Llamar a la función de enviar comentario
    }
  };

  return (
    <section id="discussion" className="mt-16 border-t border-gray-200 pt-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
        <MessageSquare className="w-6 h-6 mr-2" />
        Discussion
      </h2>

      {/* Comment Form */}
      <div className="mb-8">
        <div className="flex space-x-4">
          <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0"></div>
          <div className="flex-1">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={handleKeyDown} // Añadimos el manejador de la tecla
              placeholder="Add to the discussion..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
            ></textarea>
            <button
              onClick={handleCommentSubmit}
              disabled={isLoading}
              className="mt-2 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {isLoading ? (
                <span>Loading...</span>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Comment
                </>
              )}
            </button>
            {resultMessage}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DiscussionSection;
